/**
 * Pricing Page
 *
 * Full-page pricing experience for subscriptions and credit packs.
 * Clean design with proper spacing and visual hierarchy.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Zap,
  CreditCard,
  Check,
  Crown,
  Gift,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { Platform, openPurchasePage } from '../../utils/platform';
import { useAuth } from '../../contexts/AuthContext';
import {
  usePaymentsEnabled,
  wasBlockedByKillSwitch,
  PAYMENTS_UNAVAILABLE_MESSAGE,
} from '../../hooks/usePaymentsEnabled';

const API_BASE = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  bonusCredits: number;
  price: number;
  badge?: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  credits: number;
  price: number;
  billingPeriod: 'monthly' | 'yearly';
  monthlyEquivalent?: number;
  features: string[];
  badge?: string;
  highlight?: boolean;
}

// Credit packs - one-time purchase, expire after 2 years
// Subscriptions offer better per-credit value
const CREDIT_PACKS: CreditPack[] = [
  { id: 'credits_10', name: 'Starter', credits: 10, bonusCredits: 0, price: 35 },
  { id: 'credits_25', name: 'Growth', credits: 25, bonusCredits: 2, price: 69 },
  { id: 'credits_50', name: 'Pro', credits: 50, bonusCredits: 5, price: 129, badge: 'POPULAR' },
  { id: 'credits_100', name: 'Agency', credits: 100, bonusCredits: 15, price: 239, badge: 'BEST VALUE' },
];

const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  // Monthly Plans
  {
    id: 'starter_monthly',
    name: 'Starter',
    credits: 10,
    price: 29,
    billingPeriod: 'monthly',
    features: [
      '10 credits per month',
      'BlogCraft AI Writer',
      'Basic templates',
      'Email support',
    ],
  },
  {
    id: 'pro_monthly',
    name: 'Pro',
    credits: 50,
    price: 79,
    billingPeriod: 'monthly',
    features: [
      '50 credits per month',
      'PlanCraft Strategy Engine',
      'Editorial Calendar',
      'Priority support',
      'Advanced templates',
    ],
    badge: 'BEST VALUE',
    highlight: true,
  },
  {
    id: 'agency_monthly',
    name: 'Agency',
    credits: 150,
    price: 199,
    billingPeriod: 'monthly',
    features: [
      '150 credits per month',
      'Unlimited workspaces',
      'Team collaboration',
      'Dedicated account manager',
      'Custom integrations',
    ],
    badge: 'FOR TEAMS',
  },

  // Yearly Plans (20% discount)
  {
    id: 'starter_yearly',
    name: 'Starter',
    credits: 10,
    price: 278.40,
    billingPeriod: 'yearly',
    monthlyEquivalent: 23.20,
    features: [
      '10 credits per month',
      'BlogCraft AI Writer',
      'Basic templates',
      'Email support',
    ],
  },
  {
    id: 'pro_yearly',
    name: 'Pro',
    credits: 50,
    price: 758.40,
    billingPeriod: 'yearly',
    monthlyEquivalent: 63.20,
    features: [
      '50 credits per month',
      'PlanCraft Strategy Engine',
      'Editorial Calendar',
      'Priority support',
      'Advanced templates',
    ],
    badge: 'BEST VALUE',
    highlight: true,
  },
  {
    id: 'agency_yearly',
    name: 'Agency',
    credits: 150,
    price: 1910.40,
    billingPeriod: 'yearly',
    monthlyEquivalent: 159.20,
    features: [
      '150 credits per month',
      'Unlimited workspaces',
      'Team collaboration',
      'Dedicated account manager',
      'Custom integrations',
    ],
    badge: 'FOR TEAMS',
  },
];

interface PricingPageProps {
  onBack: () => void;
  currentCredits?: number;
  onPurchaseComplete?: () => void;
}

export const PricingPage: React.FC<PricingPageProps> = ({
  onBack,
  currentCredits = 0,
  onPurchaseComplete: _onPurchaseComplete,
}) => {
  const { getAuthToken } = useAuth();
  const { paymentsEnabled, loading: paymentsLoading } = usePaymentsEnabled();
  const [selectedItem, setSelectedItem] = useState<string>('pro_yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'subscriptions' | 'packs'>('subscriptions');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [error, setError] = useState<string | null>(null);

  const isNative = Platform.isNative();

  // Sync selected plan when billing period changes
  useEffect(() => {
    const currentPlan = SUBSCRIPTION_PLANS.find(p => p.id === selectedItem);
    if (currentPlan && currentPlan.billingPeriod !== billingPeriod) {
      // Switch to the same tier but different billing period
      const newPlan = SUBSCRIPTION_PLANS.find(
        p => p.name === currentPlan.name && p.billingPeriod === billingPeriod
      );
      if (newPlan) {
        setSelectedItem(newPlan.id);
      }
    }
  }, [billingPeriod, selectedItem]);

  // Explain a closed door. Deliberately after `paymentsLoading`, so the
  // message never flashes while the check is still in flight.
  useEffect(() => {
    if (!paymentsLoading && !paymentsEnabled) {
      setError(PAYMENTS_UNAVAILABLE_MESSAGE);
    }
  }, [paymentsLoading, paymentsEnabled]);

  const handlePurchase = async (itemId: string) => {
    // Backend-sourced payments switch; see hooks/usePaymentsEnabled.
    if (!paymentsEnabled) return;
    setIsLoading(true);
    setError(null);

    if (isNative) {
      const checkoutUrl = `https://contentsage.app/checkout?pack=${itemId}`;
      openPurchasePage(checkoutUrl);
      setIsLoading(false);
    } else {
      try {
        const token = await getAuthToken();
        if (!token) {
          setError('Please log in to purchase');
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}/api/v1/payments/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ plan_type: itemId }),
        });

        if (response.ok) {
          const data = await response.json();
          window.location.href = data.checkout_url;
        } else if (await wasBlockedByKillSwitch(response)) {
          // detail is an OBJECT for this case; passing it to setError as-is
          // would put {reason, message} into React state and throw
          // "Objects are not valid as a React child" on the next render.
          setError(
            'Purchases are paused right now. Your existing plan and credits are ' +
              'unaffected - please try again shortly.',
          );
        } else {
          const errorData = await response.json().catch(() => ({}));
          const detail = errorData.detail;
          setError(
            typeof detail === 'string'
              ? detail
              : 'Failed to create checkout session',
          );
        }
      } catch (err) {
        console.error('Failed to create checkout:', err);
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Pricing</h1>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto pb-32">
        {/* Balance Card */}
        <div className="px-4 pt-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Current Balance</p>
                <p className="text-xl font-bold text-gray-900">{currentCredits} Credits</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="px-4 pt-4">
          <div className="bg-gray-100 rounded-lg p-1 flex">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'subscriptions'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Subscriptions
            </button>
            <button
              onClick={() => setActiveTab('packs')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'packs'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Credit Packs
            </button>
          </div>
        </div>

        {/* Billing Period Toggle - Only show for subscriptions */}
        {activeTab === 'subscriptions' && (
          <div className="px-4 pt-4">
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                  billingPeriod === 'yearly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>Yearly</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                    SAVE 20%
                  </span>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Plans/Packs */}
        <div className="px-4 pt-4 space-y-3">
          {activeTab === 'subscriptions' ? (
            <>
              <p className="text-sm text-gray-500 mb-2">
                {billingPeriod === 'yearly'
                  ? 'Save 20% with annual billing'
                  : 'Flexible monthly subscriptions'}
              </p>
              {SUBSCRIPTION_PLANS.filter(plan => plan.billingPeriod === billingPeriod).map((plan) => {
                const isSelected = selectedItem === plan.id;
                const pricePerCredit = (plan.price / plan.credits).toFixed(2);

                return (
                  <motion.button
                    key={plan.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedItem(plan.id)}
                    className={`w-full text-left rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50'
                        : plan.highlight
                        ? 'border-indigo-200 bg-white'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {/* Card Header */}
                    <div className="p-4 pb-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Crown className={`w-5 h-5 ${isSelected ? 'text-indigo-600' : 'text-gray-400'}`} />
                          <span className="font-semibold text-gray-900">{plan.name}</span>
                          {plan.badge && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              {plan.badge}
                            </span>
                          )}
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>

                      {plan.billingPeriod === 'yearly' ? (
                        <>
                          <div className="flex items-baseline gap-1 mb-1">
                            <span className="text-2xl font-bold text-gray-900">${plan.price.toFixed(2)}</span>
                            <span className="text-gray-500">/year</span>
                          </div>
                          <p className="text-sm text-emerald-600 font-medium">
                            ${plan.monthlyEquivalent?.toFixed(2)}/mo · Save 20%
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-1 mb-1">
                            <span className="text-2xl font-bold text-gray-900">${plan.price}</span>
                            <span className="text-gray-500">/month</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {plan.credits} credits/month · ${pricePerCredit}/credit
                          </p>
                        </>
                      )}
                    </div>

                    {/* Features */}
                    <div className={`px-4 pb-4 pt-3 border-t ${isSelected ? 'border-indigo-200' : 'border-gray-100'}`}>
                      <ul className="space-y-2">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                            <Check className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-indigo-500' : 'text-gray-400'}`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.button>
                );
              })}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-2">
                One-time purchases, no commitment
              </p>
              {CREDIT_PACKS.map((pack) => {
                const isSelected = selectedItem === pack.id;
                const totalCredits = pack.credits + pack.bonusCredits;
                const pricePerCredit = (pack.price / totalCredits).toFixed(2);

                return (
                  <motion.button
                    key={pack.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedItem(pack.id)}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Zap className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{pack.name}</span>
                            {pack.badge && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                {pack.badge}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>{pack.credits} credits</span>
                            {pack.bonusCredits > 0 && (
                              <span className="flex items-center gap-1 text-emerald-600">
                                <Gift className="w-3 h-3" />
                                +{pack.bonusCredits} bonus
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">${pack.price}</p>
                        <p className="text-xs text-gray-400">${pricePerCredit}/credit</p>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </>
          )}
        </div>
      </main>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-3 flex items-center gap-2">
            <span className="text-red-500 text-sm font-medium flex-1 text-center">{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 text-xs font-bold">✕</button>
          </div>
        )}
        <button
          onClick={() => handlePurchase(selectedItem)}
          disabled={isLoading || !selectedItem || !paymentsEnabled}
          className="w-full py-4 bg-indigo-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              {activeTab === 'subscriptions' ? (
                <>
                  <Crown className="w-5 h-5" />
                  Subscribe Now
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Purchase Credits
                </>
              )}
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
        <p className="text-xs text-center text-gray-400 mt-2">
          {activeTab === 'subscriptions'
            ? 'Cancel anytime. Credits reset monthly.'
            : 'One-time purchase. Credits never expire.'}
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
